import { inject, Injectable } from '@angular/core';
import { ToastsService } from '@catch/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { serialize } from 'object-to-formdata';
import { of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';
import { IconAdapter } from '../../adapters/icon.adapter';
import { urlToFile } from '../../functions/urlToFile';
import {
  deleteCompanyIcon,
  deleteCompanyIconFailure,
  deleteCompanyIconSuccess,
  loadCompanyIcon,
  loadCompanyIconFailure,
  loadCompanyIconSuccess,
  updateCompanyIcon,
  updateCompanyIconFailure,
  updateCompanyIconSuccess,
} from './company-icon.actions';

@Injectable()
export class CompanyIconEffects {
  private readonly actions$ = inject(Actions);
  private readonly iconAdapter = inject(IconAdapter);
  private readonly toastsService = inject(ToastsService);
  
  loadCompanyIcon$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCompanyIcon),
      exhaustMap(() =>
        this.iconAdapter.getIcon().pipe(
          map((result) => loadCompanyIconSuccess(result)),
          catchError((error) => {
            return of(loadCompanyIconFailure({ error }));
          })
        )
      )
    )
  );

  updateCompanyIcon$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCompanyIcon),
      concatMap(async (props) => serialize({ image: await urlToFile(props.payload, 'image.png', 'image/png') })),
      mergeMap((data) =>
        this.iconAdapter.editIcon(data).pipe(
          map((result) => updateCompanyIconSuccess(result)),
          catchError((error) => {
            return of(updateCompanyIconFailure({ error }));
          })
        )
      )
    )
  );

  updateCompanyIconSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateCompanyIconSuccess),
        tap(() => {
          this.toastsService.success('company-tool.views.settings.company.design.toasts.icon-updated');
        })
      ),
    { dispatch: false }
  );

  deleteCompanyIcon$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCompanyIcon),
      mergeMap((props) =>
        this.iconAdapter.deleteIcon().pipe(
          map(() => deleteCompanyIconSuccess()),
          catchError((error) => {
            return of(deleteCompanyIconFailure({ error }));
          })
        )
      )
    )
  );

  deleteCompanyIconSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteCompanyIconSuccess),
        tap(() => {
          this.toastsService.success('company-tool.views.settings.company.design.toasts.icon-deleted');
        })
      ),
    { dispatch: false }
  );

  loadCompanyIconFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadCompanyIconFailure),
        tap((action) => {
          // Todo: Add proper error handling. Maybe with a toasty?
          //alert(action.error.message);
        })
      ),
    { dispatch: false }
  );
}
